import React, { useState, useEffect } from 'react';

import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import { Drawer, Box, Card, useTheme, IconButton, Button, Chip, Tooltip } from '@mui/material';
import { OrdIconStandard, OrdListHeaderLayout, ordReadonlyContext } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { RecordContextProvider, useTranslate, Resource, useResourceContext } from 'react-admin';
import { Route, useParams, useLocation, useBlocker } from 'react-router-dom';

import { flowchartLogger } from 'src/lib/logger';

import FlowchartReadonlyRedirect from './components/FlowchartReadonlyRedirect';
import { mouseSelectionContext } from './context/mouseSelectionContext';
import { personalEndpointContext } from './context/personalEndpointContext';
import FrontendData from './dataProvider/prepareFrontendData';
import Flow, { nodeExtent } from './Flow';
import FlowchartResourceList from './FlowchartResourceList';
import useAutoSave from './hooks/useAutoSave';
import { useIngordDataProvider } from './hooks/useIngordDataProvider';
import MenuPanel from './MenuPanel';
import NodeControlHeader from './nodes/NodeControlHeader';
import NodeControls, { getControlsComponentName } from './nodes/NodeControls';

import type { DialplanData, NodeMenuConfig } from './dataProvider/types';
import type { Node, Edge, NodeTypes } from '@xyflow/react';
import type { RaRecord } from 'react-admin';

interface ReactFlowRouteProps {
  initialNodes: Node[] | undefined;
  initialEdges: Edge[] | undefined;
  nodeTypes: NodeTypes | undefined;
  editForm: React.ReactNode;
  nodeMenuConfig: NodeMenuConfig;
  backupVersionField: string | undefined;
  toggleListVisibility: () => void;
  setSelectedNode: (node: Node) => void;
  setDrawerOpen: () => void;
  setListVisible: () => void;
  setRecord: (value: RaRecord | undefined) => void;
  recordChanged: boolean;
  setRecordChanged: (value: boolean) => void;
  ignoreNavigationPrompt: boolean;
  setIgnoreNavigationPrompt: (value: boolean) => void;
}

const ReactFlowRoute = ({
  initialNodes,
  initialEdges,
  nodeTypes,
  editForm,
  nodeMenuConfig,
  backupVersionField,
  toggleListVisibility,
  setSelectedNode,
  setDrawerOpen,
  setListVisible,
  setRecord,
  recordChanged,
  setRecordChanged,
  ignoreNavigationPrompt,
  setIgnoreNavigationPrompt,
}: ReactFlowRouteProps): React.ReactElement => {
  const reactFlowInstance = useReactFlow();
  const { recordID, '*': splat } = useParams();
  // Trailing slashes land in the splat ('show/'), and falling back to edit mode there
  // would bypass the readonly view for show-only users
  const isReadonly = splat === 'show' || splat?.startsWith('show/') === true;
  const dataProvider = useIngordDataProvider();
  const [activeRecord, setActiveRecord] = useState<RaRecord | undefined>(undefined);
  const [minimapVisible, setMiniMapVisible] = useState(false);
  const translate = useTranslate();
  const resource = useResourceContext();
  useAutoSave(recordID, recordChanged && !isReadonly);

  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!recordChanged || ignoreNavigationPrompt) return false;
    return currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (navigationBlocker.state !== 'blocked') {
      return;
    }

    const shouldProceed = window.confirm(translate('callflows.interface.unsavedExit'));
    if (shouldProceed) {
      setIgnoreNavigationPrompt(true);
      navigationBlocker.proceed();
      return;
    }

    navigationBlocker.reset();
  }, [navigationBlocker, setIgnoreNavigationPrompt, translate]);

  const setReactFlowData = React.useCallback(
    (record: RaRecord | undefined): void => {
      if (record !== undefined && 'entry' in record && 'nodes' in record) {
        const frontendData = new FrontendData({ entry: record.entry, nodes: record.nodes });
        const { nodes, edges } = frontendData.prepare();
        reactFlowInstance.setNodes(nodes);
        reactFlowInstance.setEdges(edges);
        setTimeout(() => {
          void reactFlowInstance.fitView();
        }, 0);
      }
    },
    [reactFlowInstance]
  );

  // Display an alert if closing with unsaved changes
  useEffect(() => {
    const handleTabClose = (event: BeforeUnloadEvent): void => {
      if (recordChanged) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleTabClose);

    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [recordChanged, translate]);

  // Fetch record data from the URL parameter
  useEffect(() => {
    const fetchRecord = async (): Promise<void> => {
      if (resource === undefined || recordID === undefined || recordID === 'create') {
        setRecordChanged(false);
        setIgnoreNavigationPrompt(false);
        return;
      }

      await dataProvider
        .getOne<RaRecord>(resource, { id: recordID })
        .then((result) => {
          const record = result.data;
          setActiveRecord(record);
          setRecord(record);
          setReactFlowData(record);
          localStorage.removeItem(`flowData-${recordID}`);
        })
        .catch((error) => {
          flowchartLogger.error('Failed to fetch record on route', { error });
          setActiveRecord(undefined);
        })
        .finally(() => {
          setRecordChanged(false);
          setIgnoreNavigationPrompt(false);
        });
    };

    void fetchRecord();
  }, [recordID, dataProvider, setRecord, setReactFlowData, resource, setRecordChanged, setIgnoreNavigationPrompt]);

  if (resource === undefined) {
    return <></>;
  }

  return (
    <ordReadonlyContext.Provider value={isReadonly}>
      {!isReadonly && <FlowchartReadonlyRedirect resource={resource} record={activeRecord} />}
      <FlowchartToolbar record={activeRecord} onClick={setListVisible} />
      <MenuPanel
        resource={resource}
        backupVersionField={backupVersionField}
        recordChanged={recordChanged}
        saveAlwaysEnabled={
          (initialNodes !== undefined && initialNodes.length > 0) ||
          (initialEdges !== undefined && initialEdges.length > 0)
        }
        setRecordChanged={setRecordChanged}
        toggleMinimap={() => {
          setMiniMapVisible(!minimapVisible);
        }}
        minimapVisible={minimapVisible}
        toggleListVisibility={toggleListVisibility}
        editForm={editForm}
        nodeMenuConfig={nodeMenuConfig}
        isReadonly={isReadonly}
      />
      <Flow
        initialNodes={initialNodes}
        initialEdges={initialEdges}
        minimapVisible={minimapVisible}
        nodeTypes={nodeTypes}
        recordChanged={recordChanged}
        nodeMenuConfig={nodeMenuConfig}
        setRecordChanged={setRecordChanged}
        onNodeClick={(event, node) => {
          const target = event.target as HTMLElement;
          if (target?.closest('[data-node-open-settings]') !== null) {
            setSelectedNode(node);
            setDrawerOpen();
          }
        }}
      />
    </ordReadonlyContext.Provider>
  );
};

interface AssignedE164 {
  e164_match: string;
}

const FlowchartToolbar = ({
  record,
  onClick,
}: {
  record: RaRecord | undefined;
  onClick: () => void;
}): React.ReactElement => {
  const theme = useTheme();
  const translate = useTranslate();
  const resource = useResourceContext();
  const assignedE164s = (record?.assigned_inbound_e164s as AssignedE164[] | undefined) ?? [];
  const iconName = resource === Resources.mobility_configurations ? 'mobilityConfigurations' : 'callflows';

  return (
    <Box className='flowchart-toolbar' sx={{ marginBottom: theme.spacing(4), display: 'flex', alignItems: 'center' }}>
      <Box sx={{ flexShrink: 0 }}>
        <OrdListHeaderLayout
          icon={<OrdIconStandard name={iconName} />}
          title={record !== undefined ? record.name : translate('callflows.interface.noRecordSelected')}
        />
      </Box>
      {assignedE164s.length > 0 && (
        <Tooltip title={translate('resources.inbound_e164s.name')}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {assignedE164s.map((e164) => (
              <Chip key={e164.e164_match} label={e164.e164_match} size='small' variant='outlined' sx={{ ml: 1 }} />
            ))}
          </Box>
        </Tooltip>
      )}
      <Box sx={{ flex: 1 }} />
      <IconButton onClick={onClick} title={translate('callflows.interface.toggleListVisibility')}>
        <MoreVertRoundedIcon />
      </IconButton>
    </Box>
  );
};

export interface FlowchartProps {
  initialNodes?: Node[];
  initialEdges?: Edge[];
  initialData?: DialplanData[] | undefined;
  nodeTypes?: NodeTypes | undefined;
  editForm: React.ReactNode;
  nodeMenuConfig: NodeMenuConfig;
  resource: string;
  isPersonal?: boolean;
  backupVersionField?: string;
}

export const Flowchart = ({
  initialNodes = [],
  initialEdges = [],
  initialData = undefined,
  nodeTypes = undefined,
  editForm = <></>,
  nodeMenuConfig,
  resource,
  isPersonal = false,
  backupVersionField = undefined,
}: FlowchartProps): React.ReactElement => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] = useState<Node | undefined>(undefined);
  const [listVisible, setListVisible] = React.useState<boolean>(false);
  const [useMouseSelection, setUseMouseSelection] = useState(
    localStorage.getItem('flowchartUseMouseSelection') === 'true'
  );
  const [recordChanged, setRecordChanged] = useState<boolean>(false);
  const [ignoreNavigationPrompt, setIgnoreNavigationPrompt] = useState<boolean>(false);
  const resourceContext = useResourceContext();
  const translate = useTranslate();
  const [activeFlowchartRecord, setActiveFlowchartRecord] = useState<RaRecord | undefined>(initialData?.[0]);

  const handleDrawerClose = (): void => {
    flowchartLogger.debug('Closing drawer');
    setDrawerOpen(false);
    setSelectedNode(undefined);
  };

  useEffect(() => {
    let pathName = location.pathname;
    if (pathName.endsWith('/')) {
      pathName = pathName.slice(0, -1);
    }
    if (pathName.endsWith(`/${resource}`) && (initialData === undefined || initialData.length === 0)) {
      setListVisible(true);
      setActiveFlowchartRecord(undefined);
    }
  }, [initialData, location.pathname, resource]);

  if (resourceContext === undefined) {
    return <></>;
  }

  return (
    <Box display='flex' flexDirection='column' sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <ReactFlowProvider nodeExtent={nodeExtent}>
        <personalEndpointContext.Provider value={isPersonal ?? false}>
          <mouseSelectionContext.Provider value={{ useMouseSelection, setUseMouseSelection }}>
            <RecordContextProvider value={activeFlowchartRecord}>
              <Card
                sx={{
                  flex: '1 1 auto',
                  backgroundColor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: 'none',
                }}
              >
                <Resource name={resourceContext}>
                  <Route
                    index
                    path=':recordID/*'
                    element={
                      <ReactFlowRoute
                        initialNodes={initialNodes}
                        initialEdges={initialEdges}
                        nodeTypes={nodeTypes}
                        editForm={editForm}
                        nodeMenuConfig={nodeMenuConfig}
                        toggleListVisibility={() => {
                          setListVisible(!listVisible);
                        }}
                        setSelectedNode={(node) => {
                          setSelectedNode(node);
                        }}
                        setDrawerOpen={() => {
                          setDrawerOpen(true);
                        }}
                        setListVisible={() => {
                          setListVisible(true);
                        }}
                        setRecord={setActiveFlowchartRecord}
                        backupVersionField={backupVersionField}
                        recordChanged={recordChanged}
                        setRecordChanged={setRecordChanged}
                        ignoreNavigationPrompt={ignoreNavigationPrompt}
                        setIgnoreNavigationPrompt={setIgnoreNavigationPrompt}
                      />
                    }
                  />
                  <Route
                    path='*'
                    element={
                      <>
                        <FlowchartToolbar
                          record={undefined}
                          onClick={() => {
                            setListVisible(true);
                          }}
                        />
                        <Box display='flex' justifyContent='center'>
                          <Button
                            variant='contained'
                            onClick={() => {
                              setListVisible(true);
                            }}
                          >
                            {translate('callflows.interface.selectRecord')}
                          </Button>
                        </Box>
                      </>
                    }
                  />
                </Resource>
              </Card>

              <Drawer
                anchor='right'
                open={listVisible}
                onClose={() => {
                  setListVisible(false);
                }}
                slotProps={{ paper: { sx: { borderRadius: 1, overflow: 'hidden' } } }}
              >
                <Box
                  sx={{
                    width: '800px',
                    maxWidth: '100%',
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <FlowchartResourceList
                    initialData={initialData}
                    setRecord={setActiveFlowchartRecord}
                    listVisible={listVisible}
                    resource={resource}
                    editForm={editForm}
                    backupVersionField={backupVersionField}
                    recordChanged={recordChanged}
                    setRecordChanged={setRecordChanged}
                    setIgnoreNavigationPrompt={setIgnoreNavigationPrompt}
                  />
                </Box>
              </Drawer>

              <Drawer
                data-testid='node-edit-menu'
                anchor='right'
                open={
                  drawerOpen &&
                  selectedNode?.type !== undefined &&
                  getControlsComponentName(selectedNode.type) !== false
                }
                slotProps={{ paper: { sx: { overflow: 'hidden', display: 'grid', gridTemplateRows: 'auto 1fr' } } }}
                onClose={handleDrawerClose}
              >
                <NodeControlHeader node={selectedNode} handleDrawerClose={handleDrawerClose} />
                <Box
                  sx={{
                    padding: 2,
                    width: '800px',
                    maxWidth: '100%',
                    minHeight: 0,
                    display: 'grid',
                    gridTemplateRows: '1fr',
                  }}
                >
                  {selectedNode !== undefined && (
                    <NodeControls activeNode={selectedNode} record={activeFlowchartRecord} resource={resourceContext} />
                  )}
                </Box>
              </Drawer>
            </RecordContextProvider>
          </mouseSelectionContext.Provider>
        </personalEndpointContext.Provider>
      </ReactFlowProvider>
    </Box>
  );
};
